import React from 'react'

import { ArrowRightIcon, CloudUploadIcon, UploadIcon } from 'lucide-react'
import { inject, observer } from 'mobx-react'

import DndArea from '~/client/src/desktop/components/DndArea/DndArea'
import DesktopFileInput from '~/client/src/desktop/components/FileInput/DesktopFileInput'
import DesktopInitialState from '~/client/src/desktop/stores/DesktopInitialState'
import { Button } from '~/client/src/shared/components/Button/Button'
import { FilePicker } from '~/client/src/shared/components/FilePicker'
import { Loader } from '~/client/src/shared/components/Loader'
import MenuCloser from '~/client/src/shared/components/MenuCloser'
import FileType from '~/client/src/shared/enums/FileType'
import Localization from '~/client/src/shared/localization/LocalizationManager'

import MapViewSetUpStore, {
  ACCEPTED_SITEMAP_EXTENSIONS,
} from '../../../MapViewSetUp.store'

interface IProps {
  store: MapViewSetUpStore

  state?: DesktopInitialState
}

const BASEMAP_TITLE_WIDTH = 240
const BASEMAP_TITLE_HEIGHT = 30

const EXTENSIONS_SEPARATOR = ','

@inject('state')
@observer
export default class UpdateSitemapDialog extends React.Component<IProps> {
  public render(): JSX.Element {
    const { selectedBasemap } = this.props.store.sitemapsSetupStore
    const {
      isUploading,
      basemapFile,
      basmapFileObjectUrl,
      toggleEditMenu,
      setSitemapBasemap,
      setPdfOrUpdateBasemap,
      hideEditMenu,
    } = this.props.store.sitemapControlStore
    if (!selectedBasemap) {
      return null
    }

    const isSaveButtonActive = !isUploading && !!basemapFile
    return (
      <MenuCloser
        closeMenu={toggleEditMenu}
        className="col absolute tooltip-popup brada10"
      >
        <div className="row px20 pt20 mb20">
          <div className="col">
            <div className="text large bold">
              {Localization.translator.mapFileUpload.replaceThisPlan}
            </div>
            <div>
              <DesktopFileInput
                id={selectedBasemap.id}
                name={selectedBasemap.fileName}
                value={selectedBasemap.source}
                isReadonly={true}
                textClassName="hint"
                shouldHideIconAndOutline={true}
                shouldHideName={true}
                projectId={this.props.state.activeProject.id}
              />
            </div>
            <div
              style={{
                height: BASEMAP_TITLE_HEIGHT,
                maxWidth: BASEMAP_TITLE_WIDTH,
              }}
              className="text large text-ellipsis"
            >
              {selectedBasemap.fileName}
            </div>
          </div>
          <div className="col y-center no-grow mx10">
            <ArrowRightIcon size={16} />
          </div>
          <div className="col">
            <div className="text large bold">
              {Localization.translator.mapFileUpload.withThisOne}
            </div>
            {!basemapFile ? (
              <div className="general-map-setup-dnd-area">
                <DndArea
                  fileType={FileType.Image}
                  accept={ACCEPTED_SITEMAP_EXTENSIONS}
                  title={this.title}
                  onDrop={setSitemapBasemap}
                  className="upload-schedule-dnd-container col mt20 x-center y-center"
                />
                {this.renderBrowseComputerLine()}
              </div>
            ) : (
              <>
                <DesktopFileInput
                  id="basemap-file-input"
                  name={basemapFile.name}
                  value={basmapFileObjectUrl}
                  isReadonly={true}
                  textClassName="hint"
                  shouldHideIconAndOutline={true}
                  projectId={this.props.state.activeProject.id}
                />
                {this.renderBrowseComputerLine()}
              </>
            )}
          </div>
        </div>
        {this.renderActionBottomButtons(
          Localization.translator.clear,
          this.submitTitle,
          hideEditMenu,
          setPdfOrUpdateBasemap,
          isSaveButtonActive,
        )}
      </MenuCloser>
    )
  }

  private get submitTitle(): string {
    return this.props.store.sitemapControlStore.isPdfTypeBasemapSelected
      ? Localization.translator.mapFileUpload.selectPage
      : Localization.translator.save
  }

  private renderActionBottomButtons(
    cancelText: string,
    applyText: string,
    cancelAction: () => void,
    applyAction: () => void,
    isApplyActive: boolean,
  ): JSX.Element {
    return (
      <div className="row bt-light-input-border">
        <div className="bt-input-border tw:flex tw:items-center tw:justify-around dialog-footer">
          <Button
            variant="ghost"
            className="tw:ml-5 tw:mr-2.5"
            onClick={cancelAction}
          >
            {cancelText}
          </Button>
          <Button
            className="tw:ml-2.5 tw:mr-5"
            disabled={!isApplyActive}
            onClick={applyAction}
          >
            {this.props.store.sitemapControlStore.isUploading ? (
              <Loader />
            ) : (
              applyText
            )}
          </Button>
        </div>
      </div>
    )
  }

  private get title(): string | JSX.Element {
    if (this.props.store.sitemapControlStore.isUploading) {
      return (
        <Loader hint={Localization.translator.mapFileUpload.uploadingPlan} />
      )
    }

    if (this.props.store.sitemapControlStore.whiteboardCreationPdfFile) {
      return Localization.translator.mapFileUpload.planUploaded
    }

    return (
      <div className="col text large no-grow x-center">
        <span>
          <CloudUploadIcon size={20} className="tw:align-middle" />{' '}
          {Localization.translator.mapFileUpload.dragMapHere}
        </span>
        <span>
          {Localization.translator.mapFileUpload.acceptedExtensions(
            '.pdf, .png, .tif',
          )}
        </span>
      </div>
    )
  }

  private renderBrowseComputerLine = (): JSX.Element => {
    const { basemapFile, setSitemapBasemap } =
      this.props.store.sitemapControlStore
    return (
      <FilePicker
        accept={ACCEPTED_SITEMAP_EXTENSIONS.join(EXTENSIONS_SEPARATOR)}
        onPick={([file]) => setSitemapBasemap(file)}
        render={<Button variant="outline" className="tw:w-full" />}
      >
        <UploadIcon data-icon="inline-start" />
        {basemapFile
          ? Localization.translator.change_verb
          : Localization.translator.uploadFile}
      </FilePicker>
    )
  }
}
